import React, { useMemo, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
  Box,
  Card,
  Dialog,
  Button,
  MenuItem,
  FormControl,
  Select,
  OutlinedInput,
  Checkbox,
  useMediaQuery,
  FormControlLabel,
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import {
  useCreateUserRoleMutation,
  useGetAllUserPrivilegesQuery,
} from "../../../Api/attoDeskApi";
import CloseIcon from "@mui/icons-material/Close";
import {
  IPrivilege,
  IUserRole,
  Privilege,
} from "../../../Api/Interface/api.interface";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import useIsMobile from "../../../Core/hook/useIsMobile";
import { Privileges } from "../../../Core/Enum/enum";

interface FormValues {
  id: string;
  roleName: string;
  privileges: number[];
}

interface IUserAdminPopUp {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data?: IUserRole;
}

type MenuItem = {
  title: string;
  submenuOpen: boolean;
  privilege?: string;
  privileges?: string[];
  submenus?: {
    title: string;
    privilege: string;
  }[];
};

const MainMenuList: {
  comomTitle: string;
  menuList?: MenuItem[];
}[] = [
  {
    comomTitle: "General",
    menuList: [
      {
        title: "Dashboard",
        submenuOpen: false,
        privilege: Privileges.DashboardView,
      },
      {
        title: "Purchase Dashboard",
        submenuOpen: false,
        privilege: Privileges.DashboardView,
      },
    ],
  },
  {
    comomTitle: "Purchase",
    menuList: [
      {
        title: "Purchase",
        submenuOpen: true,
        privileges: [
          Privileges.OrderList,
          Privileges.MainItemsView,
          Privileges.ItemsView,
          Privileges.CategoriesView,
          Privileges.LoopingView,
        ],
        submenus: [
          { title: "Order", privilege: Privileges.OrderList },
          { title: "Products", privilege: Privileges.MainItemsView },
          { title: "Variants", privilege: Privileges.ItemsView },
          { title: "Category", privilege: Privileges.CategoriesView },
          { title: "Looping", privilege: Privileges.LoopingView },
        ],
      },
      {
        title: "Fields",
        submenuOpen: true,
        privileges: [
          Privileges.StoreView,
          Privileges.TagView,
          Privileges.BrandView,
          Privileges.TaxView,
          Privileges.PrinterView,
        ],
        submenus: [
          { title: "Supplier", privilege: Privileges.StoreView },
          { title: "Tag", privilege: Privileges.TagView },
          { title: "Brand", privilege: Privileges.BrandView },
          { title: "Tax", privilege: Privileges.TaxView },
          { title: "Printer", privilege: Privileges.PrinterView },
        ],
      },
    ],
  },
  {
    comomTitle: "Staff",
    menuList: [
      {
        title: "Staff",
        submenuOpen: false,
        privilege: Privileges.StaffView,
      },
      {
        title: "Additional",
        submenuOpen: true,
        privileges: [Privileges.DepartmentView, Privileges.PositionView],
        submenus: [
          { title: "Department", privilege: Privileges.DepartmentView },
          { title: "Position", privilege: Privileges.PositionView },
        ],
      },
    ],
  },
  {
    comomTitle: "Schedule & Punch",
    menuList: [
      {
        title: "Clock In/Out",
        submenuOpen: false,
        privilege: Privileges.ScheduleView,
      },
      {
        title: "Schedule",
        submenuOpen: false,
        privilege: Privileges.ScheduleView,
      },
      {
        title: "Payroll",
        submenuOpen: false,
        privilege: Privileges.PayrollView,
      },
    ],
  },
  {
    comomTitle: "Other",
    menuList: [
      {
        title: "Setting",
        submenuOpen: true,
        privileges: [
          Privileges.UserView,
          Privileges.BranchView,
          Privileges.CompanyView,
          Privileges.EmailView,
          Privileges.ThemeView,
        ],
        submenus: [
          { title: "User", privilege: Privileges.UserView },
          { title: "User Role", privilege: Privileges.UserView },
          { title: "Privilege", privilege: Privileges.UserView },
          { title: "Branch", privilege: Privileges.BranchView },
          { title: "Company", privilege: Privileges.CompanyView },
          { title: "Email", privilege: Privileges.EmailView },
          { title: "Theme Color", privilege: Privileges.ThemeView },
        ],
      },
    ],
  },
];

const UserAdmin = ({
  openModel = false,
  handleCloseDialog,
  data,
}: IUserAdminPopUp) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobileRes = useIsMobile();
  const [open] = React.useState(openModel);
  const { showErrorMessage, showMessage } = useNotifier();
  const [newCompany, { isLoading }] = useCreateUserRoleMutation();
  const [selectedPrivileges, setSelectedPrivileges] = useState<number[]>([]);

  const { data: privileges, isLoading: privilegesLoading } =
    useGetAllUserPrivilegesQuery();

  const handleClose = () => {
    handleCloseDialog(false);
  };

  useEffect(() => {
    if (data?.privileges) {
      setSelectedPrivileges(data.privileges);
    }
  }, [data]);

  const privilegesList = useMemo(() => {
    return privileges?.data ?? [];
  }, [privileges?.data]);

  const privilegeMap = useMemo(() => {
    const map: Record<string, number> = {};
    privilegesList.forEach((p: any) => {
      map[p.name.toLowerCase()] = p.id;
    });
    return map;
  }, [privilegesList]);

  const getPrivilegeId = (name: string) =>
    privilegeMap[name.toLowerCase()] ?? null;

  const formik = useFormik<FormValues>({
    initialValues: {
      id: data?.id || "",
      roleName: data?.roleName || "",
      privileges: data?.privileges || [],
    },

    onSubmit: async (values) => {
      try {
        const updatedData: Partial<FormValues> = {
          roleName: toProperCase(values.roleName),
          privileges: selectedPrivileges,
        };

        if (data) {
          updatedData.id = values.id;
        }

        const response = await newCompany(updatedData).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          handleClose();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return (
      formik.values.roleName !== "" && formik.values.roleName !== undefined
    );
  }, [formik.values.roleName]);

  const handlePrivilegeChange = (id: number) => {
    setSelectedPrivileges((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handleParentChange = (ids: number[]) => {
    const allSelected = ids.every((id) => selectedPrivileges.includes(id));

    if (allSelected) {
      setSelectedPrivileges((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedPrivileges((prev) => {
        const combined = [...prev, ...ids];
        return combined.filter(
          (item, index) => combined.indexOf(item) === index
        );
      });
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedPrivileges(privilegesList.map((p: any) => p.id));
    } else {
      setSelectedPrivileges([]);
    }
  };

  const allSelected =
    privilegesList.length > 0 &&
    selectedPrivileges.length === privilegesList.length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby="alert-dialog-description"
      classes={{ paper: "inherit" }}
      fullScreen={isMobileRes}
      fullWidth={isMobileRes}
      maxWidth="lg"
    >
      <Grid className={isMobileRes ? classes.DialogMobile : classes.Dialog}>
        <Grid container spacing={3}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Card className={classes.stepperCard}>
              <Grid
                container
                justifyContent={"space-between"}
                spacing={5}
                sx={{ borderBottom: 2, padding: 2 }}
                className={classes.titleGrid}
              >
                <Grid item pt={2} alignContent={"center"}>
                  <Typography className={classes.cardTitle}>
                    {data ? "Edit" : "Add"}
                  </Typography>
                </Grid>
                <Grid
                  item
                  pt={2}
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    variant="contained"
                    className={classes.editButton}
                    onClick={handleClose}
                  >
                    <CloseIcon />
                  </Button>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <FormikProvider value={formik}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 2, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            User Role Information
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={3} p={"24px"}>
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                User Role{" "}
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="User Role"
                                size="small"
                                {...formik.getFieldProps("roleName")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 2, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            Select Privileges
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={3} p={"24px"}>
                        <Grid container spacing={3} p={"24px"}>
                          {privilegesLoading ? (
                            <Grid
                              item
                              lg={12}
                              md={12}
                              sm={12}
                              xs={12}
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minHeight: "200px",
                              }}
                            >
                              <CircularProgress />
                            </Grid>
                          ) : (
                            <>
                              <Grid item xs={12}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={allSelected}
                                      onChange={handleSelectAll}
                                    />
                                  }
                                  label="Select All"
                                />
                              </Grid>
                              {MainMenuList.map((section) => (
                                <Grid
                                  item
                                  lg={4}
                                  md={6}
                                  sm={12}
                                  xs={12}
                                  key={section.comomTitle}
                                >
                                  <Box
                                    sx={{
                                      border: "1px solid #e0e0e0",
                                      borderRadius: "4px",
                                      p: 2,
                                      mb: 2,
                                      height: "100%",
                                    }}
                                  >
                                    {/* Section Title */}
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight={700}
                                      sx={{
                                        mb: 2,
                                        color: "primary.main",
                                        borderBottom: "2px solid #eee",
                                        pb: 1,
                                      }}
                                    >
                                      {section.comomTitle}
                                    </Typography>

                                    {section.menuList?.map((menu) => {
                                      // 🔹 Single item
                                      if (menu.privilege && !menu.submenus) {
                                        const id = getPrivilegeId(
                                          menu.privilege
                                        );
                                        if (!id) return null;

                                        return (
                                          <Box
                                            key={menu.title}
                                            sx={{
                                              pl: 1,
                                              borderRadius: "4px",
                                              "&:hover": {
                                                backgroundColor: "#f5f5f5",
                                              },
                                            }}
                                          >
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  checked={selectedPrivileges.includes(
                                                    id
                                                  )}
                                                  onChange={() =>
                                                    handlePrivilegeChange(id)
                                                  }
                                                />
                                              }
                                              label={
                                                <Typography fontWeight={600}>
                                                  {menu.title}
                                                </Typography>
                                              }
                                            />
                                          </Box>
                                        );
                                      }

                                      // 🔹 Parent item
                                      if (menu.privileges && menu.submenus) {
                                        const ids = menu.privileges
                                          .map((p) => getPrivilegeId(p))
                                          .filter(
                                            (id): id is number => id !== null
                                          );

                                        if (!ids.length) return null;

                                        const allChecked = ids.every((id) =>
                                          selectedPrivileges.includes(id)
                                        );

                                        const someChecked =
                                          ids.some((id) =>
                                            selectedPrivileges.includes(id)
                                          ) && !allChecked;

                                        return (
                                          <Box
                                            key={menu.title}
                                            sx={{
                                              p: 1,
                                              borderRadius: "4px",
                                            }}
                                          >
                                            {/* Parent */}
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  checked={allChecked}
                                                  indeterminate={someChecked}
                                                  onChange={() =>
                                                    handleParentChange(ids)
                                                  }
                                                />
                                              }
                                              label={
                                                <Typography fontWeight={600}>
                                                  {menu.title}
                                                </Typography>
                                              }
                                            />

                                            {/* Submenus */}
                                            <Box
                                              sx={{
                                                ml: 4,
                                                // p: 1,
                                                // borderRadius: 2,
                                                // backgroundColor: "#f9fbff",
                                              }}
                                            >
                                              {menu.submenus.map((sub) => {
                                                const subId = getPrivilegeId(
                                                  sub.privilege
                                                );
                                                if (!subId) return null;

                                                return (
                                                  <Box
                                                    key={sub.title}
                                                    sx={{
                                                      pl: 1,
                                                      borderRadius: "4px",
                                                      "&:hover": {
                                                        backgroundColor:
                                                          "#eef3ff",
                                                      },
                                                    }}
                                                  >
                                                    <FormControlLabel
                                                      control={
                                                        <Checkbox
                                                          size="small"
                                                          checked={selectedPrivileges.includes(
                                                            subId
                                                          )}
                                                          onChange={() =>
                                                            handlePrivilegeChange(
                                                              subId
                                                            )
                                                          }
                                                        />
                                                      }
                                                      label={
                                                        <Typography
                                                          fontSize={13}
                                                        >
                                                          {sub.title}
                                                        </Typography>
                                                      }
                                                    />
                                                  </Box>
                                                );
                                              })}
                                            </Box>
                                          </Box>
                                        );
                                      }

                                      return null;
                                    })}
                                  </Box>
                                </Grid>
                              ))}
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={"24px"}>
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            className={classes.addButton}
                            onClick={handleClose}
                          >
                            Cancel
                          </Button>
                          <Box m={0.5}></Box>

                          <Button
                            variant="contained"
                            startIcon={
                              isLoading ? (
                                <CircularProgress
                                  size={18}
                                  className={classes.isLoading}
                                />
                              ) : (
                                <CheckCircleOutlineIcon />
                              )
                            }
                            className={classes.addButton}
                            onClick={() => formik.handleSubmit()}
                            disabled={!formValid || isLoading}
                          >
                            {data ? "Update" : "Create"}
                          </Button>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                </Grid>
              </form>
            </FormikProvider>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default UserAdmin;
